import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useBranch } from "../../../contexts/BranchContext";
import EditMemberModal from "./EditMemberModal";
import { useNavigate } from "react-router-dom";

import PlanLimitBanner from "../../../components/PlanLimitBanner";
import { useSubscriptionLimits } from "../../../hooks/useSubscriptionLimits";

const paymentMethodOptions = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

export default function MemberRegistration({ onMemberAdded }) {
  const { user } = useAuth();
  const { selectedBranchId, selectedBranch } = useBranch();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [packages, setPackages] = useState([]);
  const [filterPackages, setFilterPackages] = useState([]);
  const [filterTrainers, setFilterTrainers] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", dob: "",
    package: "", branch: selectedBranchId || "", startDate: "", notes: "",
    paymentAmount: "", paymentType: "full", paymentMethod: "cash", paymentNotes: "",
    password: "", confirmPassword: "", trainerId: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isLimitError, setIsLimitError] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Renewal date map: memberId -> end_date
  const [memberRenewalDates, setMemberRenewalDates] = useState({});

  // Package map: memberId -> { name, duration }
  const [memberPackageMap, setMemberPackageMap] = useState({});

  // Trainer map: trainerId -> full_name
  const [trainerMap, setTrainerMap] = useState({});

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPackageId, setFilterPackageId] = useState("all");
  const [filterTrainerId, setFilterTrainerId] = useState("all");

  // Bulk select state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Assign Workout Plan modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Assign Diet Plan modal state
  const [showAssignDietModal, setShowAssignDietModal] = useState(false);
  const [dietPlans, setDietPlans] = useState([]);
  const [loadingDietPlans, setLoadingDietPlans] = useState(false);
  const [selectedDietPlanId, setSelectedDietPlanId] = useState("");
  const [assigningDiet, setAssigningDiet] = useState(false);

  // Assign Trainer modal state
  const [showAssignTrainerModal, setShowAssignTrainerModal] = useState(false);
  const [selectedTrainerIdBulk, setSelectedTrainerIdBulk] = useState("");
  const [assigningTrainer, setAssigningTrainer] = useState(false);

  // Bulk Change Status modal state
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);

  // Bulk Update Package modal state
  const [showUpdatePackageModal, setShowUpdatePackageModal] = useState(false);
  const [bulkPackageId, setBulkPackageId] = useState("");
  const [updatingPackage, setUpdatingPackage] = useState(false);

  // Bulk Modify Fees modal state
  const [showModifyFeesModal, setShowModifyFeesModal] = useState(false);
  const [bulkCustomFee, setBulkCustomFee] = useState("");
  const [modifyingFees, setModifyingFees] = useState(false);

  // Bulk Adjust Renewal Dates modal state
  const [showAdjustRenewalModal, setShowAdjustRenewalModal] = useState(false);
  const [bulkRenewalDate, setBulkRenewalDate] = useState("");
  const [adjustingRenewal, setAdjustingRenewal] = useState(false);

  // Staff+Trainer list for form dropdown (role IN staff, trainer)
  const [staffTrainers, setStaffTrainers] = useState([]);

  // Toast state
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchPackages = async () => {
    if (!user?.id) return;
    try {
      setLoadingPackages(true);
      const { data } = await api.get('/packages', { params: { is_active: 'true' } });
      setPackages(data || []);
      setFilterPackages(data || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoadingPackages(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const params = {};
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/staff', { params });
      setFilterTrainers(data || []);
      setStaffTrainers(data || []);
    } catch (err) {
      console.error('Error fetching trainers:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoadingMembers(true);
      const memberParams = { limit: 2000 };
      if (selectedBranchId) memberParams.branch_id = selectedBranchId;
      const [membersResp, branchesResp] = await Promise.all([
        api.get('/members', { params: memberParams }),
        api.get('/branches'),
      ]);
      const memberList = membersResp?.data || [];
      setMembers(memberList);
      setBranches(branchesResp?.data || []);

      // Build renewal date map and package map from member data
      const renewalMap = {};
      const pkgMap = {};
      memberList?.forEach(m => {
        if (m?.package_end_date) renewalMap[m?.id] = m?.package_end_date;
        if (m?.package_name) pkgMap[m?.id] = { name: m?.package_name, duration: null };
      });
      setMemberRenewalDates(renewalMap);
      setMemberPackageMap(pkgMap);

      // Build trainer map from members' trainer info
      const tMap = {};
      memberList?.forEach(m => {
        if (m?.trainer_id && m?.trainer_name) tMap[m?.trainer_id] = m?.trainer_name;
      });
      setTrainerMap(tMap);
    } catch (err) {
      console.error('Error fetching member data:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchWorkoutPlans = async () => {
    try {
      setLoadingPlans(true);
      const { data } = await api.get('/workout-plans');
      setWorkoutPlans(data || []);
    } catch (err) {
      console.error('Error fetching workout plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const fetchDietPlans = async () => {
    try {
      setLoadingDietPlans(true);
      const { data } = await api.get('/diet-plans');
      setDietPlans(data || []);
    } catch (err) {
      console.error('Error fetching diet plans:', err);
    } finally {
      setLoadingDietPlans(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPackages();
    fetchTrainers();
  }, [selectedBranchId]);

  // Sync branch field whenever the form opens or the selected branch changes
  useEffect(() => {
    setForm(prev => ({ ...prev, branch: selectedBranchId || "" }));
  }, [selectedBranchId, showForm]);

  // Compute filtered members
  const filteredMembers = members?.filter((m) => {
    const fullName = `${m?.first_name || ""} ${m?.last_name || ""}`?.toLowerCase();
    const email = (m?.email || "")?.toLowerCase();
    const query = searchQuery?.toLowerCase();

    const matchesSearch = !query || fullName?.includes(query) || email?.includes(query);
    const matchesStatus = filterStatus === "all" || m?.member_status === filterStatus;
    const matchesTrainer = filterTrainerId === "all" || m?.trainer_id === filterTrainerId;

    // Package filter: check member_packages
    // We'll handle this via a separate lookup below
    return matchesSearch && matchesStatus && matchesTrainer;
  });

  // Bulk select helpers
  const allVisibleSelected = filteredMembers?.length > 0 && filteredMembers?.every(m => selectedIds?.has(m?.id));
  const someSelected = selectedIds?.size > 0;

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMembers?.map(m => m?.id)));
    }
  };

  const toggleSelectMember = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) {
        next?.delete(id);
      } else {
        next?.add(id);
      }
      return next;
    });
  };

  const handleOpenAssignModal = () => {
    setSelectedPlanId("");
    fetchWorkoutPlans();
    setShowAssignModal(true);
  };

  const handleOpenAssignDietModal = () => {
    setSelectedDietPlanId("");
    fetchDietPlans();
    setShowAssignDietModal(true);
  };

  const handleOpenAssignTrainerModal = () => {
    setSelectedTrainerIdBulk("");
    setShowAssignTrainerModal(true);
  };

  const handleOpenChangeStatusModal = () => {
    setBulkStatus("");
    setShowChangeStatusModal(true);
  };

  const handleOpenUpdatePackageModal = () => {
    setBulkPackageId("");
    setShowUpdatePackageModal(true);
  };

  const handleOpenModifyFeesModal = () => {
    setBulkCustomFee("");
    setShowModifyFeesModal(true);
  };

  const handleOpenAdjustRenewalModal = () => {
    setBulkRenewalDate("");
    setShowAdjustRenewalModal(true);
  };

  const handleAssignWorkoutPlan = async () => {
    if (!selectedPlanId || selectedIds?.size === 0) return;
    try {
      setAssigning(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map(id => api.put(`/members/${id}`, { workout_plan_id: selectedPlanId })));
      showToast(`Workout plan assigned to ${ids?.length} member${ids?.length !== 1 ? 's' : ''}`);
      setShowAssignModal(false);
      setSelectedIds(new Set());
      setSelectedPlanId("");
    } catch (err) {
      console.error('Error assigning workout plan:', err);
      showToast('Failed to assign workout plan. Please try again.', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignDietPlan = async () => {
    if (!selectedDietPlanId || selectedIds?.size === 0) return;
    try {
      setAssigningDiet(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map(id => api.put(`/members/${id}`, { diet_plan_id: selectedDietPlanId })));
      showToast(`Diet plan assigned to ${ids?.length} member${ids?.length !== 1 ? 's' : ''}`);
      setShowAssignDietModal(false);
      setSelectedIds(new Set());
      setSelectedDietPlanId("");
    } catch (err) {
      console.error('Error assigning diet plan:', err);
      showToast('Failed to assign diet plan. Please try again.', 'error');
    } finally {
      setAssigningDiet(false);
    }
  };

  const handleAssignTrainer = async () => {
    if (!selectedTrainerIdBulk || selectedIds?.size === 0) return;
    try {
      setAssigningTrainer(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map(id => api.put(`/members/${id}`, { trainer_id: selectedTrainerIdBulk })));
      showToast(`Trainer assigned to ${ids?.length} member${ids?.length !== 1 ? 's' : ''}`);
      setShowAssignTrainerModal(false);
      setSelectedIds(new Set());
      setSelectedTrainerIdBulk("");
      await fetchData();
    } catch (err) {
      console.error('Error assigning trainer:', err);
      showToast('Failed to assign trainer. Please try again.', 'error');
    } finally {
      setAssigningTrainer(false);
    }
  };

  const handleBulkChangeStatus = async () => {
    if (!bulkStatus || selectedIds?.size === 0) return;
    try {
      setChangingStatus(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map(id => api.put(`/members/${id}`, { member_status: bulkStatus })));
      showToast(`Status changed to "${bulkStatus}" for ${ids?.length} member${ids?.length !== 1 ? 's' : ''}`);
      setShowChangeStatusModal(false);
      setSelectedIds(new Set());
      setBulkStatus("");
      await fetchData();
    } catch (err) {
      console.error('Error changing status:', err);
      showToast('Failed to change status. Please try again.', 'error');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleBulkUpdatePackage = async () => {
    if (!bulkPackageId || selectedIds?.size === 0) return;
    try {
      setUpdatingPackage(true);
      const ids = Array.from(selectedIds);
      const selectedPkgObj = packages?.find(p => String(p?.id) === String(bulkPackageId));
      if (!selectedPkgObj) throw new Error('Package not found');

      // Upsert member_packages for each selected member (deactivate old, insert new)
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const endDate = new Date(today);
      const durationMonths = selectedPkgObj?.duration_value
        ? (selectedPkgObj?.duration_unit === 'days'
            ? Math.round(selectedPkgObj?.duration_value / 30)
            : selectedPkgObj?.duration_unit === 'years'
              ? selectedPkgObj?.duration_value * 12
              : selectedPkgObj?.duration_value)
        : 1;
      endDate?.setMonth(endDate?.getMonth() + durationMonths);
      const endDateStr = endDate?.toISOString()?.split('T')?.[0];

      // POST /members/:id/packages auto-deactivates old package and creates new one
      await Promise.all(ids.map(memberId => api.post(`/members/${memberId}/packages`, {
        package_id: selectedPkgObj?.id,
        package_name: selectedPkgObj?.name,
        package_type: 'monthly',
        price: selectedPkgObj?.price,
        start_date: today,
        end_date: endDateStr,
      })));

      showToast(`Package updated to "${selectedPkgObj?.name}" for ${ids?.length} member${ids?.length !== 1 ? 's' : ''}`);
      setShowUpdatePackageModal(false);
      setSelectedIds(new Set());
      setBulkPackageId("");
    } catch (err) {
      console.error('Error updating package:', err);
      showToast('Failed to update package. Please try again.', 'error');
    } finally {
      setUpdatingPackage(false);
    }
  };

  const handleBulkModifyFees = async () => {
    const feeValue = parseFloat(bulkCustomFee);
    if (isNaN(feeValue) || feeValue < 0 || selectedIds?.size === 0) return;
    try {
      setModifyingFees(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map(id => api.put(`/members/${id}`, { custom_fee: feeValue })));
      showToast(`Custom fee set to $${feeValue?.toFixed(2)} for ${ids?.length} member${ids?.length !== 1 ? 's' : ''}`);
      setShowModifyFeesModal(false);
      setSelectedIds(new Set());
      setBulkCustomFee("");
      await fetchData();
    } catch (err) {
      console.error('Error modifying fees:', err);
      showToast('Failed to modify fees. Please try again.', 'error');
    } finally {
      setModifyingFees(false);
    }
  };

  const handleBulkAdjustRenewal = async () => {
    if (!bulkRenewalDate || selectedIds?.size === 0) return;
    try {
      setAdjustingRenewal(true);
      const ids = Array.from(selectedIds);
      // Update end_date by re-creating packages with new end_date
      await Promise.all(ids.map(memberId => {
        const m = members?.find(mem => mem?.id === memberId);
        if (!m?.package_name) return Promise.resolve();
        return api.post(`/members/${memberId}/packages`, {
          package_name: m?.package_name,
          package_type: m?.package_type || 'monthly',
          price: m?.package_price || 0,
          start_date: m?.package_start_date || new Date()?.toISOString()?.split('T')?.[0],
          end_date: bulkRenewalDate,
        });
      }));
      showToast(`Renewal date updated to ${new Date(bulkRenewalDate)?.toLocaleDateString()} for ${ids?.length} member${ids?.length !== 1 ? 's' : ''}`);
      setShowAdjustRenewalModal(false);
      setSelectedIds(new Set());
      setBulkRenewalDate("");
    } catch (err) {
      console.error('Error adjusting renewal dates:', err);
      showToast('Failed to adjust renewal dates. Please try again.', 'error');
    } finally {
      setAdjustingRenewal(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === 'paymentAmount') {
      const packagePrice = selectedPkg?.price;
      let cappedValue = value;
      let newPaymentType = form.paymentType;

      if (packagePrice && value !== '') {
        const numVal = parseFloat(value);
        if (!isNaN(numVal)) {
          if (numVal >= packagePrice) {
            cappedValue = String(packagePrice);
            newPaymentType = 'full';
          } else if (numVal > 0) {
            newPaymentType = 'partial';
          }
        }
      }
      setForm(prev => ({ ...prev, paymentAmount: cappedValue, paymentType: newPaymentType }));
      return;
    }

    if (name === 'package') {
      const newPkg = packages?.find(p => String(p?.id) === String(value));
      const currentPaidAmt = parseFloat(form.paymentAmount) || 0;
      let newPaymentAmount = form.paymentAmount;
      let newPaymentType = form.paymentType;

      if (newPkg && currentPaidAmt > 0) {
        if (currentPaidAmt > newPkg.price) {
          newPaymentAmount = String(newPkg.price);
          newPaymentType = 'full';
        } else if (currentPaidAmt < newPkg.price) {
          newPaymentType = 'partial';
        } else {
          newPaymentType = 'full';
        }
      }
      setForm(prev => ({ ...prev, package: value, paymentAmount: newPaymentAmount, paymentType: newPaymentType }));
      return;
    }

    setForm(prev => ({ ...prev, [name]: value }));
  };

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    const levels = [
      { score: 0, label: "", color: "" },
      { score: 1, label: "Weak", color: "var(--color-error)" },
      { score: 2, label: "Fair", color: "var(--color-warning)" },
      { score: 3, label: "Good", color: "#3b82f6" },
      { score: 4, label: "Strong", color: "var(--color-success)" },
    ];
    return levels?.[score] || levels?.[0];
  };

  const passwordStrength = getPasswordStrength(form?.password);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!user?.id) return;

    // Check subscription limit before creating member
    const limitMsg = checkLimit("members");
    if (limitMsg) { setError(limitMsg); setIsLimitError(true); return; }
    setIsLimitError(false);

    // Password validation
    if (form?.email && form?.password) {
      if (form?.password?.length < 8) { setError("Password must be at least 8 characters."); return; }
      if (form?.password !== form?.confirmPassword) { setError("Passwords do not match."); return; }
    }

    try {
      setSubmitting(true);
      setError(null);

      const selectedPkg = packages?.find(p => String(p?.id) === String(form?.package));

      const { data: newMember } = await api.post('/members', {
        branch_id: form?.branch || null,
        first_name: form?.firstName,
        last_name: form?.lastName,
        email: form?.email || null,
        phone: form?.phone || null,
        date_of_birth: form?.dob || null,
        member_status: 'active',
        join_date: form?.startDate || new Date()?.toISOString()?.split('T')?.[0],
        notes: form?.notes || null,
        trainer_id: form?.trainerId || null,
        password: form?.email && form?.password ? form?.password : undefined,
      });

      if (selectedPkg && newMember?.id) {
        const startDate = form?.startDate || new Date()?.toISOString()?.split('T')?.[0];
        const endDate = new Date(startDate);
        const durationMonths = selectedPkg?.duration_value
          ? (selectedPkg?.duration_unit === 'days' ? Math.round(selectedPkg?.duration_value / 30) : selectedPkg?.duration_unit === 'years' ? selectedPkg?.duration_value * 12 : selectedPkg?.duration_value)
          : 1;
        endDate?.setMonth(endDate?.getMonth() + durationMonths);

        await api.post(`/members/${newMember?.id}/packages`, {
          package_name: selectedPkg?.name,
          package_type: 'monthly',
          price: selectedPkg?.price,
          start_date: startDate,
          end_date: endDate?.toISOString()?.split('T')?.[0],
        });
      }

      if (form?.paymentAmount && parseFloat(form?.paymentAmount) > 0 && newMember?.id) {
        const paidAmt = parseFloat(form?.paymentAmount);
        const packagePrice = selectedPkg?.price || 0;
        const isPartial = form?.paymentType === "partial";
        const pendingAmt = isPartial ? Math.max(0, packagePrice - paidAmt) : 0;

        await api.post('/payments', {
          member_id: newMember?.id,
          branch_id: form?.branch || null,
          amount: packagePrice || paidAmt,
          paid_amount: paidAmt,
          payment_status: isPartial ? 'partial' : 'paid',
          payment_method: form?.paymentMethod,
          notes: form?.paymentNotes || null,
          paid_date: new Date()?.toISOString()?.split('T')?.[0],
          due_date: form?.startDate || new Date()?.toISOString()?.split('T')?.[0],
          description: selectedPkg ? `${selectedPkg?.name} Membership` : 'Membership Payment',
        });
      } else if (selectedPkg && newMember?.id) {
        const startDate = form?.startDate || new Date()?.toISOString()?.split('T')?.[0];
        await api.post('/payments', {
          member_id: newMember?.id,
          branch_id: form?.branch || null,
          amount: selectedPkg?.price,
          paid_amount: 0,
          payment_status: 'pending',
          due_date: startDate,
          description: `${selectedPkg?.name} Membership`,
        });
      }

      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      setForm({ firstName: "", lastName: "", email: "", phone: "", dob: "", package: "", branch: selectedBranchId || "", startDate: "", notes: "", paymentAmount: "", paymentType: "full", paymentMethod: "cash", paymentNotes: "", password: "", confirmPassword: "", trainerId: "" });
      setShowForm(false);
      await fetchData();
      refetchLimits();
      onMemberAdded?.();
    } catch (err) {
      console.error('Error registering member:', err);
      setError(err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPkg = packages?.find(p => String(p?.id) === String(form?.package));
  const paidAmt = parseFloat(form?.paymentAmount) || 0;
  const pendingPreview = form?.paymentType === "partial" && selectedPkg && paidAmt > 0
    ? Math.max(0, selectedPkg?.price - paidAmt)
    : null;

  const statusColors = {
    active: 'var(--color-success)',
    inactive: 'var(--color-error)',
    suspended: 'var(--color-warning)',
    trial: 'var(--color-primary)',
    archived: '#9ca3af',
  };

  const getBMICategory = (bmi) => {
    if (!bmi) return null;
    const val = parseFloat(bmi);
    if (val < 18.5) return "Underweight";
    if (val < 25) return "Normal";
    if (val < 30) return "Overweight";
    return "Obese";
  };

  const bmiCategoryColor = (cat) => {
    const map = { Underweight: "#3b82f6", Normal: "#22c55e", Overweight: "#f59e0b", Obese: "#ef4444" };
    return map?.[cat] || "var(--color-muted-foreground)";
  };

  const hasActiveFilters = searchQuery || filterStatus !== "all" || filterPackageId !== "all" || filterTrainerId !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setFilterStatus("all");
    setFilterPackageId("all");
    setFilterTrainerId("all");
  };

  const { checkLimit, refetch: refetchLimits } = useSubscriptionLimits(user?.id);

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium font-[var(--font-caption)] transition-all ${
          toast?.type === 'error' ?'bg-[var(--color-error)] text-white' :'bg-[var(--color-success)] text-white'
        }`}>
          <Icon name={toast?.type === 'error' ? "AlertCircle" : "CheckCircle"} size={15} color="white" />
          {toast?.message}
        </div>
      )}
      <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
        <div>
          <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Member Registration</h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
            {loadingMembers ? 'Loading...' : `${filteredMembers?.length} of ${members?.length} members`}
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
        >
          <Icon name={showForm ? "X" : "UserPlus"} size={13} color="white" />
          {showForm ? "Cancel" : "Add Member"}
        </button>
      </div>
      {submitted && (
        <div className="mx-4 mt-4 flex items-center gap-2 p-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-md">
          <Icon name="CheckCircle" size={16} color="var(--color-success)" />
          <span className="text-sm text-[var(--color-success)] font-medium font-[var(--font-caption)]">Member registered successfully!</span>
        </div>
      )}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 space-y-4 border-b border-[var(--color-border)] bg-[var(--color-muted)]/20">
          {error && isLimitError ? (
            <PlanLimitBanner message={error} />
          ) : error ? (
            <div className="flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <span className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{error}</span>
            </div>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["firstName","First Name"],["lastName","Last Name"]]?.map(([name, label]) => (
              <div key={name}>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">{label} <span className="text-[var(--color-error)]">*</span></label>
                <input required type="text" name={name} value={form?.[name]} onChange={handleChange} placeholder={label} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Email</label>
              <input type="email" name="email" value={form?.email} onChange={handleChange} placeholder="member@email.com" className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Phone</label>
              <input type="tel" name="phone" value={form?.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Date of Birth</label>
              <input type="date" name="dob" value={form?.dob} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
          </div>

          {/* Password fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                Password <span className="text-[var(--color-muted-foreground)] font-normal">(optional)</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form?.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  className="w-full px-3 py-2 pr-9 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                >
                  <Icon name={showPassword ? "EyeOff" : "Eye"} size={14} />
                </button>
              </div>
              {form?.password && (
                <div className="mt-1.5 space-y-1">
                  <div className="flex gap-1">
                    {[1,2,3,4]?.map(i => (
                      <div
                        key={i}
                        className="h-1 flex-1 rounded-full transition-all"
                        style={{ backgroundColor: i <= passwordStrength?.score ? passwordStrength?.color : 'var(--color-border)' }}
                      />
                    ))}
                  </div>
                  {passwordStrength?.label && (
                    <p className="text-xs font-[var(--font-caption)]" style={{ color: passwordStrength?.color }}>
                      {passwordStrength?.label}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={form?.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  className="w-full px-3 py-2 pr-9 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                >
                  <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={14} />
                </button>
              </div>
              {form?.confirmPassword && form?.password !== form?.confirmPassword && (
                <p className="text-xs text-[var(--color-error)] mt-1 font-[var(--font-caption)]">Passwords do not match</p>
              )}
              {form?.confirmPassword && form?.password === form?.confirmPassword && form?.password?.length >= 8 && (
                <p className="text-xs text-[var(--color-success)] mt-1 font-[var(--font-caption)]">✓ Passwords match</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Branch</label>
              {selectedBranchId ? (
                <input
                  type="text"
                  readOnly
                  value={selectedBranch?.name || ""}
                  className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-muted)]/50 text-[var(--color-foreground)] cursor-not-allowed"
                />
              ) : (
                <select name="branch" value={form?.branch} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
                  <option value="">Select Branch</option>
                  {branches?.map(b => <option key={b?.id} value={b?.id}>{b?.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Start Date <span className="text-[var(--color-error)]">*</span></label>
              <input required type="date" name="startDate" value={form?.startDate} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Membership Package</label>
            {loadingPackages ? (
              <div className="flex items-center gap-2 p-3 text-xs text-[var(--color-muted-foreground)]">
                <Icon name="Loader" size={13} color="var(--color-muted-foreground)" />
                Loading packages...
              </div>
            ) : packages?.length === 0 ? (
              <p className="text-xs text-[var(--color-muted-foreground)] p-3 border border-dashed border-[var(--color-border)] rounded-md">No packages found. Add packages from the Packages tab.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {packages?.map(pkg => (
                  <label key={pkg?.id} className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${
                    form?.package === String(pkg?.id) ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-[var(--color-border)] hover:border-[var(--color-primary)]/50"
                  }`}>
                    <input type="radio" name="package" value={String(pkg?.id)} checked={form?.package === String(pkg?.id)} onChange={handleChange} className="mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)]">
                        {pkg?.name} — <span className="font-[var(--font-data)]">${pkg?.price}</span>
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        {pkg?.duration_value ? `${pkg?.duration_value} ${pkg?.duration_unit || 'months'}` : ''}{pkg?.description ? (pkg?.duration_value ? ` · ${pkg?.description}` : pkg?.description) : ''}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Payment Section */}
          <div className="border border-[var(--color-border)] rounded-md p-4 bg-[var(--color-card)] space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="CreditCard" size={14} color="var(--color-primary)" />
              <h4 className="text-xs font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Payment Details</h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                  Amount
                  {selectedPkg && (
                    <span className="ml-1.5 text-[var(--color-muted-foreground)] font-normal">
                      (max: ${Number(selectedPkg.price).toLocaleString()})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--color-muted-foreground)] font-[var(--font-data)]">$</span>
                  <input
                    type="number"
                    name="paymentAmount"
                    value={form?.paymentAmount}
                    onChange={handleChange}
                    placeholder={selectedPkg ? selectedPkg?.price : "0.00"}
                    min="0"
                    max={selectedPkg?.price || undefined}
                    step="0.01"
                    className="w-full pl-6 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Payment Type</label>
                <div className="flex gap-2">
                  {[["full","Full"],["partial","Partial"]]?.map(([val, lbl]) => {
                    const isPartialForced = val === 'full' && selectedPkg && paidAmt > 0 && paidAmt < selectedPkg.price;
                    return (
                      <label key={val} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md border text-xs font-medium font-[var(--font-caption)] transition-colors ${
                        isPartialForced
                          ? "border-[var(--color-border)] text-[var(--color-muted-foreground)] opacity-40 cursor-not-allowed"
                          : form?.paymentType === val
                            ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)] cursor-pointer"
                            : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/40 cursor-pointer"
                      }`}>
                        <input type="radio" name="paymentType" value={val} checked={form?.paymentType === val} onChange={handleChange} disabled={isPartialForced} className="sr-only" />
                        {lbl}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Payment Method</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {paymentMethodOptions?.map(({ value, label }) => (
                  <label key={value} className={`flex items-center justify-center gap-1.5 py-2 rounded-md border cursor-pointer text-xs font-medium font-[var(--font-caption)] transition-colors ${
                    form?.paymentMethod === value
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                      : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/40"
                  }`}>
                    <input type="radio" name="paymentMethod" value={value} checked={form?.paymentMethod === value} onChange={handleChange} className="sr-only" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Payment Notes</label>
              <input
                type="text"
                name="paymentNotes"
                value={form?.paymentNotes}
                onChange={handleChange}
                placeholder="Optional notes about this payment..."
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            {pendingPreview !== null && pendingPreview > 0 && (
              <div className="flex items-center gap-2 p-2.5 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 rounded-md">
                <Icon name="AlertTriangle" size={13} color="var(--color-warning)" />
                <span className="text-xs text-[var(--color-warning)] font-medium font-[var(--font-caption)]">
                  Pending balance: <strong>${pendingPreview?.toFixed(2)}</strong> will be tracked
                </span>
              </div>
            )}
          </div>

          <button type="submit" disabled={submitting} className="w-full py-2.5 text-sm font-semibold bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60">
            {submitting ? 'Registering...' : 'Register Member'}
          </button>
        </form>
      )}
      {/* Search & Filter Bar */}
      <div className="p-3 border-b border-[var(--color-border)] bg-[var(--color-muted)]/10 space-y-2">
        {/* Search */}
        <div className="relative">
          <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            placeholder="Search by name or email..."
            className="w-full pl-8 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[var(--color-muted)] transition-colors"
            >
              <Icon name="X" size={11} color="var(--color-muted-foreground)" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap gap-2 items-center">
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="trial">Trial</option>
            <option value="archived">Archived</option>
          </select>

          {/* Package Type Filter */}
          <select
            value={filterPackageId}
            onChange={(e) => setFilterPackageId(e?.target?.value)}
            className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          >
            <option value="all">All Packages</option>
            {filterPackages?.map((pkg) => (
              <option key={pkg?.id} value={pkg?.id}>{pkg?.name}</option>
            ))}
          </select>

          {/* Trainer Filter */}
          <select
            value={filterTrainerId}
            onChange={(e) => setFilterTrainerId(e?.target?.value)}
            className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          >
            <option value="all">All Trainers</option>
            {filterTrainers?.map((t) => (
              <option key={t?.id} value={t?.id}>{t?.full_name || t?.email}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
            >
              <Icon name="X" size={11} color="currentColor" />
              Clear
            </button>
          )}
        </div>
      </div>
      {/* Bulk Action Bar */}
      {someSelected && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--color-primary)]/8 border-b border-[var(--color-primary)]/20">
          <span className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-primary)]">
            {selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleOpenAssignModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
            >
              <Icon name="Dumbbell" size={12} color="white" />
              Assign Workout Plan
            </button>
            <button
              onClick={handleOpenAssignDietModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
            >
              <Icon name="Salad" size={12} color="white" />
              Assign Diet Plan
            </button>
            <button
              onClick={handleOpenAssignTrainerModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors"
            >
              <Icon name="UserCheck" size={12} color="white" />
              Assign Trainer
            </button>
            <button
              onClick={handleOpenChangeStatusModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
            >
              <Icon name="ToggleLeft" size={12} color="white" />
              Change Status
            </button>
            <button
              onClick={handleOpenUpdatePackageModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors"
            >
              <Icon name="Package" size={12} color="white" />
              Update Package
            </button>
            <button
              onClick={handleOpenModifyFeesModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-rose-600 text-white rounded-md hover:bg-rose-700 transition-colors"
            >
              <Icon name="DollarSign" size={12} color="white" />
              Modify Fees
            </button>
            <button
              onClick={handleOpenAdjustRenewalModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
            >
              <Icon name="CalendarClock" size={12} color="white" />
              Adjust Renewal
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
            >
              <Icon name="X" size={11} color="currentColor" />
              Clear
            </button>
          </div>
        </div>
      )}
      {/* Members List */}
      <div className="overflow-x-auto">
        {loadingMembers ? (
          <div className="p-6 flex items-center justify-center">
            <Icon name="Loader2" size={16} color="var(--color-muted-foreground)" className="animate-spin" />
          </div>
        ) : filteredMembers?.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            {hasActiveFilters ? "No members match the current filters." : "No members yet. Register your first member."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
                <th className="px-4 py-2.5 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="w-3.5 h-3.5 rounded border-[var(--color-border)] accent-[var(--color-primary)] cursor-pointer"
                    title={allVisibleSelected ? "Deselect all" : "Select all"}
                  />
                </th>
                {["Name", "Email", "Status", "Join Date", "Package", "Trainer", "Renewal Date", "BMI", "Actions"]?.map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {filteredMembers?.map(m => (
                <tr
                  key={m?.id}
                  className={`hover:bg-[var(--color-muted)]/30 transition-colors ${selectedIds?.has(m?.id) ? 'bg-[var(--color-primary)]/5' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(m?.id)}
                      onChange={() => toggleSelectMember(m?.id)}
                      className="w-3.5 h-3.5 rounded border-[var(--color-border)] accent-[var(--color-primary)] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-[var(--color-foreground)] font-[var(--font-body)] whitespace-nowrap">
                    {m?.first_name} {m?.last_name}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{m?.email || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium font-[var(--font-caption)] capitalize" style={{ color: statusColors?.[m?.member_status] || "#6b7280" }}>
                      {m?.member_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">
                    {m?.join_date ? new Date(m?.join_date)?.toLocaleDateString() : '—'}
                  </td>
                  {/* Package column */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {memberPackageMap?.[m?.id] ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold font-[var(--font-body)] text-[var(--color-foreground)]">
                          {memberPackageMap?.[m?.id]?.name}
                        </span>
                        {memberPackageMap?.[m?.id]?.duration && (
                          <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                            {memberPackageMap?.[m?.id]?.duration}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">—</span>
                    )}
                  </td>
                  {/* Trainer/Staff column */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {m?.trainer_id && trainerMap?.[m?.trainer_id] ? (
                      <span className="text-xs font-medium font-[var(--font-body)] text-[var(--color-foreground)]">
                        {trainerMap?.[m?.trainer_id]}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] bg-[var(--color-muted)]">
                        Unassigned
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {memberRenewalDates?.[m?.id] ? (() => {
                      const endDate = new Date(memberRenewalDates[m?.id]);
                      const today = new Date();
                      const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                      const isExpired = diffDays < 0;
                      const isUrgent = diffDays >= 0 && diffDays <= 7;
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-bold font-[var(--font-data)] text-[var(--color-foreground)]">
                            {endDate?.toLocaleDateString()}
                          </span>
                          <span className={`text-[10px] font-semibold text-white font-[var(--font-caption)] ${isExpired ? 'text-[var(--color-error)]' : isUrgent ? 'text-[var(--color-warning)]' : 'text-[var(--color-success)]'}`}>
                            {isExpired ? `Expired ${Math.abs(diffDays)}d ago` : isUrgent ? `${diffDays}d left` : `${diffDays}d left`}
                          </span>
                        </div>
                      );
                    })() : (
                      <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {m?.bmi ? (
                      <div className="flex items-center gap-1.5">
                        {(() => {
                          const cat = getBMICategory(m?.bmi);
                          const color = bmiCategoryColor(cat);
                          return (
                            <>
                              <span className="text-xs font-bold font-[var(--font-data)]" style={{ color }}>
                                {parseFloat(m?.bmi)?.toFixed(1)}
                              </span>
                              <span
                                className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-white font-[var(--font-caption)]"
                                style={{ backgroundColor: color }}
                              >
                                {cat}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setEditingMember(m)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[var(--color-primary)] border border-[var(--color-primary)]/30 rounded-md hover:bg-[var(--color-primary)]/10 transition-colors"
                    >
                      <Icon name="Pencil" size={11} color="var(--color-primary)" />
                      Edit
                    </button>
                    <button
                      onClick={() => navigate(`/member-profile/${m?.id}`)}
                      className="flex items-center gap-1 px-2 py-1 mt-1 text-xs font-medium text-[var(--color-secondary)] border border-[var(--color-secondary)]/30 rounded-md hover:bg-[var(--color-secondary)]/10 transition-colors"
                    >
                      <Icon name="User" size={11} color="var(--color-secondary)" />
                      Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          branches={branches}
          onClose={() => setEditingMember(null)}
          onSaved={() => { fetchData(); onMemberAdded?.(); }}
        />
      )}
      {/* Assign Workout Plan Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <Icon name="Dumbbell" size={16} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Assign Workout Plan</h3>
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors"
              >
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Assigning to <strong className="text-[var(--color-foreground)]">{selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''}</strong>. This will update their workout plan.
              </p>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1.5">
                  Select Workout Plan <span className="text-[var(--color-error)]">*</span>
                </label>
                {loadingPlans ? (
                  <div className="flex items-center gap-2 p-3 text-xs text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md">
                    <Icon name="Loader2" size={13} color="var(--color-muted-foreground)" className="animate-spin" />
                    Loading workout plans...
                  </div>
                ) : workoutPlans?.length === 0 ? (
                  <p className="text-xs text-[var(--color-muted-foreground)] p-3 border border-dashed border-[var(--color-border)] rounded-md">
                    No workout plans found. Create plans from the Staff/Trainer interface.
                  </p>
                ) : (
                  <select
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  >
                    <option value="">— Choose a plan —</option>
                    {workoutPlans?.map(plan => (
                      <option key={plan?.id} value={plan?.id}>
                        {plan?.title}{plan?.profiles?.full_name ? ` · ${plan?.profiles?.full_name}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignWorkoutPlan}
                disabled={!selectedPlanId || assigning || workoutPlans?.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigning ? (
                  <>
                    <Icon name="Loader2" size={12} color="white" className="animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Icon name="Check" size={12} color="white" />
                    Confirm Assignment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Assign Diet Plan Modal */}
      {showAssignDietModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <Icon name="Salad" size={16} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Assign Diet Plan</h3>
              </div>
              <button
                onClick={() => setShowAssignDietModal(false)}
                className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors"
              >
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Assigning to <strong className="text-[var(--color-foreground)]">{selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''}</strong>. This will update their diet plan.
              </p>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1.5">
                  Select Diet Plan <span className="text-[var(--color-error)]">*</span>
                </label>
                {loadingDietPlans ? (
                  <div className="flex items-center gap-2 p-3 text-xs text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md">
                    <Icon name="Loader2" size={13} color="var(--color-muted-foreground)" className="animate-spin" />
                    Loading diet plans...
                  </div>
                ) : dietPlans?.length === 0 ? (
                  <p className="text-xs text-[var(--color-muted-foreground)] p-3 border border-dashed border-[var(--color-border)] rounded-md">
                    No diet plans found. Create plans from the Staff/Trainer interface.
                  </p>
                ) : (
                  <select
                    value={selectedDietPlanId}
                    onChange={(e) => setSelectedDietPlanId(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  >
                    <option value="">— Choose a plan —</option>
                    {dietPlans?.map(plan => (
                      <option key={plan?.id} value={plan?.id}>
                        {plan?.title}{plan?.profiles?.full_name ? ` · ${plan?.profiles?.full_name}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowAssignDietModal(false)}
                className="px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignDietPlan}
                disabled={!selectedDietPlanId || assigningDiet || dietPlans?.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigningDiet ? (
                  <>
                    <Icon name="Loader2" size={12} color="white" className="animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Icon name="Check" size={12} color="white" />
                    Confirm Assignment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Assign Trainer Modal */}
      {showAssignTrainerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <Icon name="UserCheck" size={16} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Assign Staff / Trainer</h3>
              </div>
              <button
                onClick={() => setShowAssignTrainerModal(false)}
                className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors"
              >
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Assigning to <strong className="text-[var(--color-foreground)]">{selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''}</strong>. This will update their assigned trainer.
              </p>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1.5">
                  Select Staff / Trainer <span className="text-[var(--color-error)]">*</span>
                </label>
                {staffTrainers?.length === 0 ? (
                  <p className="text-xs text-[var(--color-muted-foreground)] p-3 border border-dashed border-[var(--color-border)] rounded-md">
                    No staff or trainers found. Add them from the Staff Roster tab.
                  </p>
                ) : (
                  <select
                    value={selectedTrainerIdBulk}
                    onChange={(e) => setSelectedTrainerIdBulk(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  >
                    <option value="">— Choose staff / trainer —</option>
                    {staffTrainers?.map(st => (
                      <option key={st?.id} value={st?.id}>
                        {st?.full_name || st?.email} ({st?.role})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowAssignTrainerModal(false)}
                className="px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignTrainer}
                disabled={!selectedTrainerIdBulk || assigningTrainer || staffTrainers?.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigningTrainer ? (
                  <>
                    <Icon name="Loader2" size={12} color="white" className="animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Icon name="Check" size={12} color="white" />
                    Confirm Assignment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Change Membership Status Modal */}
      {showChangeStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <Icon name="ToggleLeft" size={16} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Change Membership Status</h3>
              </div>
              <button onClick={() => setShowChangeStatusModal(false)} className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Updating status for <strong className="text-[var(--color-foreground)]">{selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''}</strong>.
              </p>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1.5">
                  New Status <span className="text-[var(--color-error)]">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'active', label: 'Active', color: 'var(--color-success)' },
                    { value: 'inactive', label: 'Inactive', color: 'var(--color-error)' },
                    { value: 'suspended', label: 'Suspended', color: 'var(--color-warning)' },
                    { value: 'trial', label: 'Trial', color: 'var(--color-primary)' },
                  ]?.map(({ value, label, color }) => (
                    <label
                      key={value}
                      className={`flex items-center gap-1.5 p-3 rounded-md border cursor-pointer transition-colors ${
                        bulkStatus === value
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                          : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="bulkStatus"
                        value={value}
                        checked={bulkStatus === value}
                        onChange={(e) => setBulkStatus(e?.target?.value)}
                        className="sr-only"
                      />
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)]">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowChangeStatusModal(false)}
                className="px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkChangeStatus}
                disabled={!bulkStatus || changingStatus}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {changingStatus ? (
                  <><Icon name="Loader2" size={12} color="white" className="animate-spin" />Updating...</>
                ) : (
                  <><Icon name="Check" size={12} color="white" />Apply Status</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Update Package Modal */}
      {showUpdatePackageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <Icon name="Package" size={16} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Update Package</h3>
              </div>
              <button onClick={() => setShowUpdatePackageModal(false)} className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Reassigning package for <strong className="text-[var(--color-foreground)]">{selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''}</strong>. Current active packages will be deactivated.
              </p>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1.5">
                  Select New Package <span className="text-[var(--color-error)]">*</span>
                </label>
                {packages?.length === 0 ? (
                  <p className="text-xs text-[var(--color-muted-foreground)] p-3 border border-dashed border-[var(--color-border)] rounded-md">
                    No packages found. Add packages from the Packages tab.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {packages?.map(pkg => (
                      <label
                        key={pkg?.id}
                        className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${
                          bulkPackageId === String(pkg?.id)
                            ? 'border-sky-500 bg-sky-500/5' :'border-[var(--color-border)] hover:border-sky-400/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="bulkPackage"
                          value={String(pkg?.id)}
                          checked={bulkPackageId === String(pkg?.id)}
                          onChange={(e) => setBulkPackageId(e?.target?.value)}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)]">
                            {pkg?.name} — <span className="font-[var(--font-data)]">${pkg?.price}</span>
                          </p>
                          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                            {pkg?.duration_value ? `${pkg?.duration_value} ${pkg?.duration_unit || 'months'}` : ''}
                            {pkg?.description ? (pkg?.duration_value ? ` · ${pkg?.description}` : pkg?.description) : ''}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowUpdatePackageModal(false)}
                className="px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpdatePackage}
                disabled={!bulkPackageId || updatingPackage || packages?.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingPackage ? (
                  <><Icon name="Loader2" size={12} color="white" className="animate-spin" />Updating...</>
                ) : (
                  <><Icon name="Check" size={12} color="white" />Apply Package</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modify Fees Modal */}
      {showModifyFeesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <Icon name="DollarSign" size={16} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Modify Fees</h3>
              </div>
              <button onClick={() => setShowModifyFeesModal(false)} className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Set a custom fee override for <strong className="text-[var(--color-foreground)]">{selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''}</strong>. This overrides the package price for billing purposes.
              </p>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1.5">
                  Custom Fee Amount <span className="text-[var(--color-error)]">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-data)]">$</span>
                  <input
                    type="number"
                    value={bulkCustomFee}
                    onChange={(e) => setBulkCustomFee(e?.target?.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-full pl-7 pr-3 py-2.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
                <p className="text-xs text-[var(--color-muted-foreground)] mt-1.5 font-[var(--font-caption)]">
                  Enter 0 to remove the custom fee override.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowModifyFeesModal(false)}
                className="px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkModifyFees}
                disabled={bulkCustomFee === '' || isNaN(parseFloat(bulkCustomFee)) || parseFloat(bulkCustomFee) < 0 || modifyingFees}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-rose-600 text-white rounded-md hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {modifyingFees ? (
                  <><Icon name="Loader2" size={12} color="white" className="animate-spin" />Saving...</>
                ) : (
                  <><Icon name="Check" size={12} color="white" />Apply Fee</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Adjust Renewal Dates Modal */}
      {showAdjustRenewalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <Icon name="CalendarClock" size={16} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Adjust Renewal Dates</h3>
              </div>
              <button onClick={() => setShowAdjustRenewalModal(false)} className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Set a new renewal/expiry date for <strong className="text-[var(--color-foreground)]">{selectedIds?.size} member{selectedIds?.size !== 1 ? 's' : ''}</strong>. Updates the end date on their active membership package.
              </p>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1.5">
                  New Renewal Date <span className="text-[var(--color-error)]">*</span>
                </label>
                <input
                  type="date"
                  value={bulkRenewalDate}
                  onChange={(e) => setBulkRenewalDate(e?.target?.value)}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                  className="w-full px-3 py-2.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowAdjustRenewalModal(false)}
                className="px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAdjustRenewal}
                disabled={!bulkRenewalDate || adjustingRenewal}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {adjustingRenewal ? (
                  <><Icon name="Loader2" size={12} color="white" className="animate-spin" />Saving...</>
                ) : (
                  <><Icon name="Check" size={12} color="white" />Apply Date</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
